import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRequest } from "hooks/useRequest";
import { Loader2 } from "lucide-react";
import { ResearchReportPageContent } from "./ResearchReportPageContent";
import {
  fetchResearchReport,
  fetchAgentBoqExtraction,
  fetchManifest,
} from "../utils/fetchResearchReport";

export const ResearchReportPage = () => {
  const navigate = useNavigate();
  const { id: projectId = "" } = useParams();
  const { t, i18n } = useTranslation();

  const { isLoading, data, error } = useRequest(
    async () => {
      if (!projectId) return null;
      // Fetch manifest first to know available languages
      const manifest = await fetchManifest(projectId);
      const lang = i18n.language?.split("-")[0]; // "de-DE" → "de"
      const availLangs = manifest?.available_languages;

      const [report, boqExtraction] = await Promise.all([
        fetchResearchReport(projectId, lang, availLangs),
        fetchAgentBoqExtraction(projectId),
      ]);
      return { report, boqExtraction, manifest };
    },
    { manual: false, defaultParams: [] },
  );

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !data?.report) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">
            {t("smartSurveyor.researchReport.noReportAvailable")}
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {t("smartSurveyor.common.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <ResearchReportPageContent
      projectId={projectId}
      report={data.report}
      boqExtraction={data.boqExtraction}
      manifest={data.manifest}
      onBack={() => navigate(`/project/${projectId}/smart-surveyor`)}
    />
  );
};
